import "./styles.scss";

import { Plugins, Capacitor, Device, SplashScreen, App } from "@capacitor/core";
import { FCM } from "@capacitor-community/fcm";

// Import network monitoring
import { initNetworkListener, addNetworkListener, getNetworkStatus } from "./utils/network";
import RetryPopup from "./components/RetryPopup/RetryPopup.vue";

const { PushNotifications } = Plugins;

const SideMenu = () => import("./components/side-menu/index.vue");
const SearchModal = () => import("./components/search-modal/index.vue");

export default {
  name: "app",

  components: {
    "side-menu": SideMenu,
    RetryPopup,
  },

  data() {
    return {
      main_pages: [
        {
          title: "Home",
          slug: "action",
        },
        {
          title: "Annotations",
          slug: "annotations",
        },
        {
          title: "Account",
          slug: "account",
        },
      ],

      active_tab: "",
      animation_name: "next",
      fcm: new FCM(),
      fcmToken: false,
      update_poll: false,
    };
  },

  computed: {
    desktop() {
      return this.$store.state.desktop;
    },

    disableMenu() {
      let windowSize = !this.desktop ? true : false;
      return windowSize;
    },

    hideMenu() {
      if (!this.disableMenu) {
        return !this.$store.state.activeMenu;
      } else {
        return this.disableMenu;
      }
    },

    online() {
      return this.$store.state.online_status;
    },

    subscription() {
      return this.$store.getters.getActiveSubscription;
    },

    canSearch() {
      if (this.subscription && this.subscription.permissions) {
        return this.subscription.permissions.includes("search");
      }
      return false;
    },
  },

  watch: {
    $route(to, from) {
      this.animation_name = this.$store.state.animation;
      this.$store.commit("setAnimation", "next");

      let name = to.meta.tab;
      this.makeActive(name);

      this.$store.commit("setPreviousPage", from);
    },

    online(newStatus, oldStatus) {
      // device internet connection established, update content
      if (oldStatus === false && newStatus === true) {
        this.updateData();
        this.new_connection = true;
      }
    },
  },

  created() {
    console.log("✅ App component created");
    console.log("🔍 Checking $retryPopup availability:", this.$retryPopup);

    this.$store.dispatch("getUserQuota");
    this.$store.dispatch("getSubscription");

    this.$store
      .dispatch("checkAuthorization")
      .then(() => {
        if (window.location.pathname === "/") {
          this.$router.push({ path: "/action" });
        }
      })
      .catch(() => {
        this.$router.push({ path: "/" });
      });

    window.addEventListener("resize", this.resize);
    this.resize();

    this.setupNotifications();
  },

  mounted() {
    console.log("✅ App component mounted");
    console.log("🔍 $retryPopup state:", this.$retryPopup);
    // IMPORTANT: Reset retry popup state on mount
    if (this.$retryPopup) {
      this.$retryPopup.visible = false;
      this.$retryPopup.pendingAction = null;
      this.$retryPopup.onCancelCallback = null;
      this.$retryPopup.message = "";
      console.log("🔄 Retry popup state reset");
    }

    window.addEventListener("orientationchange", this.resize.bind());
    App.addListener("appStateChange", this.appResume);

    this.update_poll = setTimeout(() => {
      this.updateData();
    }, 300000);

    this.$store.dispatch("getUserQuota");
    this.$store.dispatch("getSubscription");
    this.$store.dispatch("checkOnlineStatus");
    this.resize();
    SplashScreen.hide();

    // Initialize network monitoring with toast notifications
    initNetworkListener();
    this.setupNetworkRetrySync();
    console.log("✅ Network listener initialized");
  },

  beforeDestroy() {
    clearInterval(this.update_poll);
  },

  methods: {
    appResume({ isActive }) {
      if (isActive) {
        console.info("App is active");
        this.updateData();
      }
    },

    resize() {
      const height = window.innerHeight,
        width = window.innerWidth;
      this.$store.commit("setDesktop", width >= 768);
      this.$store.commit("setHeight", height);
      this.$store.commit("setWidth", width);
    },

    onlineStatusUpdated() {
      this.$store.dispatch("checkOnlineStatus");
    },

    checkActive(slug) {
      if (this.active_tab === slug) {
        return true;
      } else {
        return false;
      }
    },

    makeActive(slug = "") {
      this.$nextTick(() => {
        setTimeout(() => {
          this.active_tab = slug;
        }, 50);
      });
    },

    updateData() {
      if (!this.$store.state.authorization) {
        return;
      }
      const active = this.$store.getters.getActiveSubscription;
      if (active && active.id) {
        this.$store.dispatch("updateData");
      }
    },

    handleNotification(data) {
      console.info(data);
    },

    setupNotifications() {
      try {
        PushNotifications.requestPermission().then((result) => {
          if (result.granted) {
            PushNotifications.register();
          }
        });
        PushNotifications.addListener("registration", (data) => {
          this.fcm
            .getToken()
            .then((result) => {
              this.fcmToken = result.token;
              this.$store.commit("setFCMToken", this.fcmToken);
            })
            .catch((err) => console.log(err));
        });
        PushNotifications.addListener(
          "pushNotificationReceived",
          this.handleNotification.bind(),
        );
      } catch (e) {
        console.warn("Notification setup error:", e);
      }
    },

    search() {
      this.$ionic.modalController
        .create({
          component: SearchModal,
        })
        .then((m) => m.present());
    },

    setupNetworkRetrySync() {
      getNetworkStatus().then((status) => {
        this.$store.commit("setOnlineStatus", status.connected);
      });

      addNetworkListener(async (status) => {
        const wasOffline = !this.$store.state.online_status;
        this.$store.commit("setOnlineStatus", status.connected);

        if (status.connected && wasOffline) {
          if (this.$retryPopup && this.$retryPopup.pendingAction) {
            await this.$onNetworkReconnect();
          }
          this.$store.dispatch("handleOnlineStatusChange");
        }
      });
    },

    async handleRetry() {
      if (this.$root && typeof this.$root.$handleRetry === "function") {
        await this.$root.$handleRetry();
      }
    },

    handleRetryCancel() {
      if (this.$root && typeof this.$root.$handleRetryCancel === "function") {
        this.$root.$handleRetryCancel();
      }
    },
  },
};
